import { useEffect, useState } from "react";

/**
 * Tracks which of the given section ids is currently most visible, favouring
 * the top-most one once it crosses the upper third of the viewport — the same
 * feel as Notion's / most docs sites' side-nav.
 */
export const useScrollSpy = (ids: string[]): string | null => {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (!els.length) return;

    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio);
          } else {
            visible.delete(entry.target.id);
          }
        });
        if (!visible.size) return;
        const top = [...visible.entries()].sort((a, b) => b[1] - a[1])[0];
        setActive(top[0]);
      },
      { rootMargin: "-15% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);

  return active;
};
